import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/apiClient';
import { Button } from '../ui/button';
import { AlertTriangle, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * iter1268 · Mid-history gap banner for CONNECTED accounts.
 * Renders ONLY when the backend proves (from the bank's own stated
 * balances) that transactions are missing between two synced days.
 * Silent for quiet accounts, Plaid-only history, or anything uncertain.
 */
const HistoryGapBanner = ({ bankAccountId, onUploadClick }) => {
  const [gaps, setGaps] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/banking/${bankAccountId}/history-gaps`);
      setGaps(data.gaps || []);
    } catch {
      setGaps([]);
    }
  }, [bankAccountId]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (gapKey) => {
    try {
      await apiClient.post(`/banking/${bankAccountId}/history-gaps/dismiss`, { gap_key: gapKey });
      setGaps((g) => g.filter((x) => x.gap_key !== gapKey));
      toast.success('Gap dismissed. You can re-check anytime from support.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dismiss');
    }
  };

  if (!gaps.length) return null;

  return (
    <div className="mt-2 space-y-2" data-testid={`history-gap-banner-${bankAccountId}`}>
      {gaps.map((g) => (
        <div
          key={g.gap_key}
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
          data-testid={`history-gap-item-${g.gap_key}`}
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">
              Missing transactions between {g.after_date} and {g.before_date}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Your bank's own balances show a {g.amount_missing >= 0 ? '+' : ''}
              {Number(g.amount_missing).toLocaleString(undefined, { minimumFractionDigits: 2 })}{' '}
              movement in this window that isn't in your books. Upload the bank
              statement covering this period to fill it.
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="sm"
              className="h-7 px-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs"
              onClick={onUploadClick}
              data-testid={`history-gap-upload-btn-${g.gap_key}`}
            >
              <Upload className="w-3 h-3 mr-1" /> Upload statement
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-1.5 text-amber-700 hover:bg-amber-100"
              onClick={() => dismiss(g.gap_key)}
              title="Dismiss"
              data-testid={`history-gap-dismiss-btn-${g.gap_key}`}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default HistoryGapBanner;
